import { shallowEqual } from 'react-redux'

import useSelector from '@hooks/useSelector'

/**
 * Epoch milliseconds keyed by entity key, e.g. `{ adCampaigns: 1757376000000 }`.
 * Sourced from `entities._updates`, which the `lastModified` middleware stamps
 * whenever an entity is updated or deleted.
 */
export type EntityUpdatedAtTimestamps = Record<string, number>

export type Options = {
  comparisonKey?: string,
  customUseSelectorFn?: typeof useSelector,
}

export type UseWatchEntityUpdatesPayload = {
  /** Timestamps of the watched entity keys that have updates. Not the entities themselves. */
  updatedEntities: EntityUpdatedAtTimestamps,
  updatedEntityKeys: string[],
}

function useWatchEntityUpdates(watchEntityKeys: string[], options: Options = {}): UseWatchEntityUpdatesPayload {
  const { comparisonKey, customUseSelectorFn } = options || {}

  const useSelectorFn = customUseSelectorFn || useSelector

  // `entities._updates` is replaced with a new object on every entity update, so selecting
  // it directly re-renders every consumer of this hook whenever any entity changes. Select
  // only the timestamps we watch, and compare them shallowly, so we re-render on our keys.
  const selectKeys = comparisonKey ? [...watchEntityKeys, comparisonKey] : watchEntityKeys

  const entityUpdates: EntityUpdatedAtTimestamps = useSelectorFn(
    (reduxState) => {
      // `EntitiesState` declares `_updates` as `{}`; typing it accurately there breaks
      // `deepmerge` in the entities reducer, so assert the real shape here instead
      // eslint-disable-next-line no-underscore-dangle
      const allEntityUpdates = reduxState.entities._updates as EntityUpdatedAtTimestamps

      return selectKeys.reduce((acc: EntityUpdatedAtTimestamps, key) => {
        const updatedAt = allEntityUpdates[key]

        // Omit keys that have never been updated, matching `Object.keys(_updates)`
        if (updatedAt !== undefined){
          acc[key] = updatedAt
        }

        return acc
      }, {})
    },
    shallowEqual,
  )

  // 0 when `comparisonKey` has no timestamp yet, which makes every watched key clear the check
  // below. That deliberately degrades to "report every update" rather than "report none": a
  // comparison entity that never updates would otherwise stall consumers permanently, since
  // they drive their `useMemo`s off the timestamps we return here.
  const comparisonUpdate = comparisonKey && entityUpdates[comparisonKey] ? entityUpdates[comparisonKey] : 0

  const updatedEntityKeys = Object.keys(entityUpdates).filter((key) => {
    const entitiesUpdatedAt = entityUpdates[key]

    // With a `comparisonKey`, only count the key as updated when its timestamp is more than
    // 500 ms later than `comparisonKey`'s (not when it was updated more than 500 ms ago)
    const isStale = comparisonKey ? (entitiesUpdatedAt - comparisonUpdate) > 500 : true

    return (watchEntityKeys.includes(key) && isStale)
  })

  const updatedEntities = updatedEntityKeys.reduce((acc: EntityUpdatedAtTimestamps, key) => {
    acc[key] = entityUpdates[key]
    return acc
  }, {})

  return {
    updatedEntities,
    updatedEntityKeys,
  }
}

export default useWatchEntityUpdates
